import API_BASE from './apiBase';

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || 'Invalid response' };
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Fetch that survives free-tier cold starts: retries on network errors with backoff
 * so a sleeping server (waking up ~30-60s) does not immediately show an error.
 */
async function fetchWithRetry(url, options = {}, { retries = 3, timeoutMs = 30000, backoffMs = 2500 } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timer);
      return res;
    } catch (err) {
      clearTimeout(timer);
      lastErr = err;
      if (attempt < retries) await sleep(backoffMs * (attempt + 1));
    }
  }
  throw lastErr;
}

/** Wake a sleeping free-tier server before the user submits (best effort, ignores errors). */
export async function warmUpApi() {
  try {
    await fetchWithRetry(`${API_BASE}/api/health`, { method: 'GET' }, { retries: 2, timeoutMs: 30000, backoffMs: 3000 });
    return true;
  } catch {
    return false;
  }
}

export async function adminPasswordLogin(email, password) {
  const res = await fetchWithRetry(`${API_BASE}/api/auth/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await parseJson(res);
  return { ok: res.ok, status: res.status, data };
}

export async function adminVerifyMfa(tempToken, code) {
  const res = await fetch(`${API_BASE}/api/auth/admin/mfa/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tempToken, code }),
  });
  const data = await parseJson(res);
  return { ok: res.ok, status: res.status, data };
}

export async function adminResendEmailOtp(tempToken) {
  const res = await fetch(`${API_BASE}/api/auth/admin/mfa/resend-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tempToken }),
  });
  const data = await parseJson(res);
  return { ok: res.ok, status: res.status, data };
}

export async function fetchAuthMe(token) {
  const res = await fetch(`${API_BASE}/api/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await parseJson(res);
  return { ok: res.ok, data };
}

export async function adminDisableMfa(token, password) {
  const res = await fetch(`${API_BASE}/api/auth/admin/mfa/disable`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ password }),
  });
  const data = await parseJson(res);
  return { ok: res.ok, data };
}
